import React from "react";
import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { SurveyDefinition as SurveyDefinition } from "./survey";

export const BUSINESS_SURVEY_NAME = "business-survey";

export const COLLECTION_FOR_OTHER_BUSINESSES_SLUG = "consumer-collection-for-other-businesses";

export const physicalLocationQuestion = (): SurveyQuestionProps => {
  const slug = "business-physical-location";

  return {
    type: "boolean",
    slug: slug,
    question:
      "Does your business have a physical presence in California where it interacts directly with consumers, like a storefront?",
    helpText:
      "If you have a physical presence in California but you only collect personal data from employees (e.g., an office building) and do not collect data directly from your B2C (or B2B if applicable) customers at that physical location, answer “No.”",
    settingsQuestionLabel: "Has a physical presence in California",
  };
};

export const businessKidsUnder18Question = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-18",
  question: "Does your business knowingly collect the personal data of minors under the age of 18?",
  helpText:
    'Answer "Yes" if your products, services, and/or website are aimed at minors under 18 or if you have actual knowledge that specific consumers are under 18 (for example, if your service systematically collects users\' birthdays).',
  settingsQuestionLabel: "Offers products/services aimed at minors under 18",
});

export const businessKidsUnder18DirectCollectionQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-18-direct-collection",
  question: "Does your business collect data directly from minors under 18?",
  helpText:
    'Answer "No" if you collect data about minors under 18 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 18 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from minors under 18",
});

export const businessKidsQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids",
  question:
    "Does your business knowingly collect the personal data of children under the age of 16?",
  helpText:
    "Answer “Yes” if your products, services, and/or website are aimed at kids under 16 or if you have actual knowledge that specific consumers are under 16 (for example, if your service systematically collects users’ birthdays).",
  settingsQuestionLabel: "Offers products/services aimed at kids under 16",
});

export const businessKidsDirectCollectionQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-direct-collection",
  question: "Does your business collect data directly from children under 16?",
  helpText:
    'Answer “No” if you collect data about children under 16 only from their parents or legal guardians. Answer "Yes" if you ask for consumers\' age (e.g., birthday with year) and continue collecting data from consumers even when they indicate that they are under 16 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from children under 16",
});

export const KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG =
  "business-products-aimed-at-kids-under-13";
export const KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG = "business-products-aimed-at-kids";
export const businessKidsUnder13Question = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG,
  question: "Does your business knowingly collect personal data of children under the age of 13?",
  helpText:
    "Answer “Yes” if your business knowingly collects data from consumers under 13 years old.",
  settingsQuestionLabel: "Offers products/services aimed at kids under 13",
});

export const businessKidsUnder13DirectCollectionQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-13-direct-collection",
  question: "Does your business collect data directly from children under 13?",
  helpText:
    'Answer “No” if you collect data about children under 13 only from their parents or legal guardians. Answer "Yes" if you collect consumers\' age (e.g., birthday with year) and collect data from consumers even when they indicate that they are under 13 years old. If you currently collect age but do not need to for any business purposes, you can discontinue the collection of age and answer "no" here.',
  settingsQuestionLabel: "Collects data directly from children under 13",
});

export const businessKidsUnder13ParentalConsentQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-products-aimed-at-kids-under-13-parental-consent",
  question:
    "When you collect the data of children who you know are under the age of 13, do you obtain parental consent to collect their data before doing so?",
  helpText:
    "Several states require parental consent before collecting data of minors under 13. If you do not currently do this, we'll create an item on your to-do list for you to implement consent requirements.",
  settingsQuestionLabel: "Parental consent for kids under 13",
});

export const businessMobileAppsQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-has-mobile-apps",
  question: "Does your business have a mobile application?",
  helpText:
    "Answer “yes” if you have an app that consumers can download from the Apple App Store or Google Play store.",
  settingsQuestionLabel: "Has a mobile application",
});

export const businessCaliforniaNonenglishQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-california-nonenglish",
  question:
    "Does your business regularly communicate with consumers in any languages other than English?",
  helpText:
    "Answer “yes” if your business regularly sends marketing emails to consumers in other languages, or if its contracts, terms or agreements are ordinarily provided in other languages.",
  settingsQuestionLabel: "Communicates with consumers in other languages",
});

export const businessTenMillionCaliforniansQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-california-10million",
  question:
    "Does your business have 10 million or more customers in California and/or get 10 million or more unique website visitors from California each year?",
  helpText: null,
});

export const MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG =
  "business-maryland-sensitive-data-compliance";
export const businessMarylandSensitiveDataComplianceQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG,
  question:
    'Maryland law prohibits your business from collecting this data unless it is strictly necessary to provide a product or service requested by the consumer whose data you collected. Maryland law also prohibits you from selling this data to others for monetary gain. Answer "yes" below if your business complies with these rules. If you answer "no," we\'ll create a task for you to complete that gets your business in compliance.',
  settingsQuestionLabel: "Maryland sensitive data compliance",
  onlyAnswerableFromDashboard: true,
});

export const businessServiceProviderQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-service-provider",
  question:
    "Does your business provide a service to other businesses (i.e., is it a “service provider”)?",
  settingsQuestionLabel: "Is a service provider",

  helpText: (
    <>
      Answer “no” if you sell products to consumers, either directly or through a marketplace. An
      example of a service provider is a B2B company like TrueVault or Salesforce.{" "}
      <a
        href="https://knowledge.truevault.com/article/285-is-your-business-a-service-provider"
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        Read more about "service providers."
      </a>
    </>
  ),
});

export const sellInExchangeForMoneyQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: "sell-in-exchange-for-money",
    question: "Does your business sell personal information to others in exchange for money?",
    helpText: <></>,
  };
};

export const businessCollectionForOtherBusinessesQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
  question:
    "Does your business collect (or purchase) personal information about individuals who it does not have a direct relationship with?",
  helpText: (
    <>
      For example, answer 'yes' if your business receives contact information about website visitors
      from a{" "}
      <a
        href="https://www.truevault.com/learn/essentials/what-is-a-data-broker"
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        data broker
      </a>{" "}
      like Retention.com or a tool like Wunderkind.
    </>
  ),
});

export const createProfilesAboutConsumersQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: "create-profiles-about-consumers",
    question: (
      <>
        <i>It’s exceedingly rare,</i> but does your business use automated profiling to make
        decisions having legal or similarly significant effects on consumers?
      </>
    ),
    helpText: (
      <>
        “Similarly significant effects” include decisions by your business to provide or deny a
        person money or credit, housing, insurance, education enrollment, criminal justice,
        employment opportunities, health care services, or access to basic necessities, such as food
        and water.
      </>
    ),
    settingsQuestionLabel: "Uses automated profiling with legal or similar effects",
  };
};

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessHipaaCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-hipaa-compliant",
  header: "Health Insurance Portability and Accountability Act (HIPAA)",
  question:
    "Is your business a “covered entity” or “business associate” that handles protected health information (PHI)?",
  helpText:
    "E.g., a healthcare provider, health plan, healthcare clearinghouse, or a business associate that processes PHI on behalf of another entity.",
  settingsQuestionLabel: "Collects data regulated by HIPAA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessGlbaCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-glba-compliant",
  header: "Gramm-Leach-Bliley Act (GLBA)",
  question:
    "Is your business a financial institution that provides a personal financial service or product?",
  helpText:
    "Eg., banks, securities brokers, insurance agents, finance companies, and even auto dealers if they extend credit, arrange financing, or give financial advice.",
  settingsQuestionLabel: "Collects data regulated by GLBA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const businessFcraCompliantQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "business-fcra-compliant",
  header: "Fair Credit Reporting Act (FCRA)",
  question:
    "Does your business share consumer personal information with consumer reporting agencies or receive said information from them?",
  helpText:
    "E.g., a property rental company provides data about evictions to a credit bureau; a car dealership pulls a credit report for a potential buyer.",
  settingsQuestionLabel: "Collects data regulated by FCRA",
});

// Note: this question was removed from the business survey as part of Product Unification Part III (POL-5891), but leaving
// the question details here since there are existing orgs that have already answered this question
export const BUSINESS_CONSUMER_HEALTH_QUESTION_SLUG = "business-identify-consumer-health";
export const BUSINESS_CONSUMER_HEALTH_CG_QUESTION_SLUG =
  "business-identify-consumer-health-collection-groups";
export const BUSINESS_CONSUMER_HEALTH_PIC_KEY = "protected-class-medical-condition";
export const businessConsumerHealthIdQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: BUSINESS_CONSUMER_HEALTH_QUESTION_SLUG,
  question:
    "Does your business use any consumer data it collects or stores to identify a consumer's physical or mental health condition or diagnosis?",
  helpText:
    "For example, answer “Yes” if your business uses browsing or purchase information to determine a consumer's potential fitness level, pregnancy status, fertility, risk of anxiety, stress, diabetes, or any other condition related to a consumer's physical or mental health.",
});

const businessQuestions = (_answers: Record<string, string>): SurveyQuestionProps[] => {
  // When adding or removing a question slug, also update SurveySlugs.BUSINESS_SURVEY_SLUGS on the backend
  return [
    physicalLocationQuestion(), // "business-physical-location"
    businessKidsUnder18Question(), // business-products-aimed-at-kids-under-18
    businessKidsUnder18DirectCollectionQuestion(), // business-products-aimed-at-kids-under-18-direct-collection
    businessKidsQuestion(), // business-products-aimed-at-kids
    businessKidsDirectCollectionQuestion(), // business-products-aimed-at-kids-direct-collection
    businessKidsUnder13Question(), // business-products-aimed-at-kids-under-13
    businessKidsUnder13DirectCollectionQuestion(), // business-products-aimed-at-kids-under-13-direct-collection
    businessKidsUnder13ParentalConsentQuestion(), // business-products-aimed-at-kids-under-13-parental-consent
    businessMobileAppsQuestion(), // business-has-mobile-apps
    businessCaliforniaNonenglishQuestion(), // business-california-nonenglish
    businessTenMillionCaliforniansQuestion(), // business-california-10million
    businessMarylandSensitiveDataComplianceQuestion(), // business-maryland-sensitive-data-compliance

    businessCollectionForOtherBusinessesQuestion(), // consumer-collection-for-other-businesses
    businessServiceProviderQuestion(), // business-service-provider
    sellInExchangeForMoneyQuestion(), // sell-in-exchange-for-money
    createProfilesAboutConsumersQuestion(), // create-profiles-about-consumers
  ];
};

const businessVisibility = (answers: Record<string, string>): Record<string, boolean> => {
  // When updating visibility, also update OrganizationOnboardingProgressService.countUnansweredBusinessQuestions()
  return {
    "business-physical-location": true,
    "business-products-aimed-at-kids-under-18": true,
    "business-products-aimed-at-kids-under-18-direct-collection":
      answers["business-products-aimed-at-kids-under-18"] == "true",
    "business-products-aimed-at-kids": true,
    "business-products-aimed-at-kids-direct-collection":
      answers["business-products-aimed-at-kids"] == "true",
    [KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG]:
      answers["business-products-aimed-at-kids"] == "true",
    "business-products-aimed-at-kids-under-13-direct-collection":
      answers["business-products-aimed-at-kids-under-13"] == "true",
    "business-products-aimed-at-kids-under-13-parental-consent":
      answers["business-products-aimed-at-kids-under-13"] == "true",
    "business-has-mobile-apps": true,
    "business-california-nonenglish": true,
    "business-california-10million": true,
    [COLLECTION_FOR_OTHER_BUSINESSES_SLUG]: true,
    "business-service-provider": true,
    "sell-in-exchange-for-money": true,
    "create-profiles-about-consumers": true,
    [MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG]: true,
  };
};

export const businessSurvey = (answers: SurveyAnswers): SurveyDefinition => {
  // Determine which questions should be disabled (locked to "Yes") based on lower age bracket answers
  const under13KnowinglyCollect = answers[KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG] == "true";
  const under16KnowinglyCollect = answers["business-products-aimed-at-kids"] == "true";
  const under13DirectCollection =
    answers["business-products-aimed-at-kids-under-13-direct-collection"] == "true";
  const under16DirectCollection =
    answers["business-products-aimed-at-kids-direct-collection"] == "true";

  // Build disabled reasons - if a string is provided, it acts as both the disabled flag AND the tooltip
  const disabled: Record<string, boolean | string> = {};

  // Lock under-16 "knowingly collect" to Yes if under-13 is Yes
  if (under13KnowinglyCollect) {
    disabled["business-products-aimed-at-kids"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 13.';
  }

  // Lock under-18 "knowingly collect" to Yes if under-16 OR under-13 is Yes
  if (under13KnowinglyCollect) {
    disabled["business-products-aimed-at-kids-under-18"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 13.';
  } else if (under16KnowinglyCollect) {
    disabled["business-products-aimed-at-kids-under-18"] =
      'This is locked to "Yes" because you indicated that you knowingly collect data from children under 16.';
  }

  // Lock under-16 "direct collection" to Yes if under-13 direct collection is Yes
  if (under13DirectCollection) {
    disabled["business-products-aimed-at-kids-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 13.';
  }

  // Lock under-18 "direct collection" to Yes if under-16 OR under-13 direct collection is Yes
  if (under13DirectCollection) {
    disabled["business-products-aimed-at-kids-under-18-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 13.';
  } else if (under16DirectCollection) {
    disabled["business-products-aimed-at-kids-under-18-direct-collection"] =
      'This is locked to "Yes" because you indicated that you collect data directly from children under 16.';
  }

  return {
    questions: businessQuestions(answers),
    visibility: businessVisibility(answers),
    relatedQuestionGroups: [
      ["business-physical-location"],
      [
        "business-products-aimed-at-kids-under-18",
        "business-products-aimed-at-kids-under-18-direct-collection",
        "business-products-aimed-at-kids",
        "business-products-aimed-at-kids-direct-collection",
        KIDS_UNDER_13_DATA_COLLECTION_QUESTION_SLUG,
        "business-products-aimed-at-kids-under-13-direct-collection",
        "business-products-aimed-at-kids-under-13-parental-consent",
      ],
      ["business-has-mobile-apps"],
      ["business-california-nonenglish"],
      ["business-california-10million"],
      [COLLECTION_FOR_OTHER_BUSINESSES_SLUG],
      ["business-service-provider"],
      ["sell-in-exchange-for-money"],
      ["create-profiles-about-consumers"],
      [MARYLAND_SENSITIVE_DATA_COMPLIANCE_QUESTION_SLUG],
    ],
    disabled,
  };
};
